import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  HeartIcon,
  ShareIcon,
  PlusIcon,
  CheckCircleIcon,
  TrashIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon, HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Tour } from '../../types';
import { CustomSafariCartItem, SelectedTour } from '../../types/customSafari';
import { useTheme } from '../../contexts/ThemeContext';
import { useSafariBuilder } from '../../contexts/SafariBuilderContext';
import { customSafariService } from '../../services/customSafariService';
import { userAuthService } from '../../services/userAuthService';
import { useAuth } from '../../contexts/AuthContext';
import UserAuthModal from '../CustomSafari/UserAuthModal';
import { usePricing } from '../../contexts/PricingContext';
import { pricingService } from '../../services/pricingService';
import { currencyService } from '../../services/currencyService';
import { useToast } from '../../contexts/ToastContext';
import { PricingTier, TieredPackage, FinalPrice } from '../../types';
import CountdownTimer from '../Common/CountdownTimer';
import { isFuture, isPast } from '../../utils/dateUtils';

interface TourCardProps {
  tour: Tour;
  onToggleFavorite?: (tourId: string) => void;
  isFavorite?: boolean;
  showQuickBook?: boolean;
  onAddToSafariBuilder?: (tour: Tour) => void;
}

const TourCard: React.FC<TourCardProps> = ({
  tour,
  onToggleFavorite,
  isFavorite = false,
  showQuickBook = true,
  onAddToSafariBuilder
}) => {
  const navigate = useNavigate();
  const { actualTheme } = useTheme();
  const { openBuilder, isBuilderOpen, addTourToBuilder } = useSafariBuilder();
  const { user: currentUser, isAuthenticated, refreshUser } = useAuth();
  const { showWarning, showError, showSuccess } = useToast();
  const [isInCart, setIsInCart] = useState(false);
  const [cartItemId, setCartItemId] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const pricingContext = usePricing();
  // Destructure for better React reactivity
  const { tier: contextTier, nationalityType, displayCurrency, countryCode, pax } = pricingContext;
  // Each card owns its own tier — starts from global context but changes independently
  const [selectedTier, setSelectedTier] = useState<PricingTier>(contextTier);
  const [calculatedPricing, setCalculatedPricing] = useState<FinalPrice | null>(null);
  const [unitCalculatedPricing, setUnitCalculatedPricing] = useState<FinalPrice | null>(null);
  const [originalUnitPricing, setOriginalUnitPricing] = useState<FinalPrice | null>(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [priceKey, setPriceKey] = useState(0); // triggers fade animation on tier change
  const hasTierVariants = tour.tierVariants && Object.keys(tour.tierVariants).length > 0;
  const hasDiscount = hasTierVariants && !!tour.discountedTierVariants?.[selectedTier];

  // Only sync from global context on first mount — do NOT override per-card selection
  const hasSyncedOnce = React.useRef(false);
  useEffect(() => {
    if (!hasSyncedOnce.current) {
      setSelectedTier(contextTier);
      hasSyncedOnce.current = true;
    }
  }, [contextTier]);

  const handleTierChange = (e: React.MouseEvent | React.ChangeEvent, tier: PricingTier) => {
    e.preventDefault();
    e.stopPropagation();
    if (tier === selectedTier) return;
    setSelectedTier(tier);
    setPriceKey(k => k + 1); // trigger fade animation
  };

  // Dynamic Pricing Calculation
  useEffect(() => {
    const calculatePrice = async () => {
      if (!hasTierVariants) {
        setCalculatedPricing(null);
        setUnitCalculatedPricing(null);
        setOriginalUnitPricing(null);
        return;
      }

      const tieredPkg: TieredPackage = {
        ...tour,
        name: tour.title,
        overview: tour.description,
        gallery: tour.images,
        durationDays: parseInt(tour.duration) || 1,
        destinations: [tour.location.name],
        status: 'PUBLISHED',
        tierVariants: tour.tierVariants!,
        createdAt: tour.createdAt || new Date().toISOString(),
        updatedAt: tour.updatedAt || new Date().toISOString()
      };

      const contextWithTier = { ...pricingContext, tier: selectedTier };
      const unitContext = { ...contextWithTier, pax: { adults: 1, children: 0 } };

      // Try Synchronous Path First (Zero Latency)
      const syncTotal = pricingService.calculatePackagePriceSync(tieredPkg, contextWithTier);
      const syncUnit = pricingService.calculatePackagePriceSync(tieredPkg, unitContext);
      
      if (syncTotal && syncUnit) {
        setCalculatedPricing(syncTotal);
        setUnitCalculatedPricing(syncUnit);
        
        if (tour.discountedTierVariants?.[selectedTier]) {
          const originalContext = { ...unitContext, useOriginalPricing: true };
          const syncOriginal = pricingService.calculatePackagePriceSync(tieredPkg, originalContext);
          if (syncOriginal) setOriginalUnitPricing(syncOriginal);
        } else {
          setOriginalUnitPricing(null);
        }
        
        setIsLoadingPrice(false);
        // We still trigger async calculation in background to ensure season accuracy & breakdown completion
      } else {
        setIsLoadingPrice(true);
      }

      try {
        // 1. Calculate Cumulative Total
        const totalResult = await pricingService.calculatePackagePrice(tieredPkg, contextWithTier);
        setCalculatedPricing(totalResult);

        // 2. Calculate Unit Price (1 Adult)
        const unitResult = await pricingService.calculatePackagePrice(tieredPkg, unitContext);
        setUnitCalculatedPricing(unitResult);

        // 3. Calculate Original (undiscounted) unit price if discount exists
        if (tour.discountedTierVariants?.[selectedTier]) {
          const originalContext = { ...unitContext, useOriginalPricing: true };
          const originalUnitResult = await pricingService.calculatePackagePrice(tieredPkg, originalContext);
          setOriginalUnitPricing(originalUnitResult);
        } else {
          setOriginalUnitPricing(null);
        }

      } catch (error) {
        console.error('Error calculating tour price:', error);
      } finally {
        setIsLoadingPrice(false);
      }
    };

    calculatePrice();
  }, [tour, nationalityType, displayCurrency, countryCode, selectedTier, pax, hasTierVariants]);

  // Check if tour is in cart
  useEffect(() => {
    if (currentUser && isAuthenticated) {
      checkIfInCart();
    } else {
      setIsInCart(false);
    }

    const handleCartUpdate = () => {
      if (currentUser && isAuthenticated) {
        checkIfInCart();
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('storage', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('storage', handleCartUpdate);
    };
  }, [tour.id, currentUser, isAuthenticated]);

  const checkIfInCart = async () => {
    if (!currentUser || !isAuthenticated) {
      setIsInCart(false);
      return;
    }

    try {
      const cart = await customSafariService.getCart(currentUser.id);
      const foundItem = cart.find((item: CustomSafariCartItem) =>
        item.itinerary.tours.some((st: SelectedTour) => st.tour.id === tour.id)
      );

      if (foundItem) {
        setIsInCart(true);
        setCartItemId(foundItem.id);
      } else {
        setIsInCart(false);
        setCartItemId(null);
      }
    } catch (error) {
      console.error('Error checking cart:', error);
      setIsInCart(false);
    }
  };

  const [isAdding, setIsAdding] = useState(false);

  const handleAddToSafari = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isAdding) return;
    setIsAdding(true);

    if (!currentUser || !isAuthenticated) {
      refreshUser().catch(() => { });
      setShowAuthModal(true);
      setIsAdding(false);
      return;
    }

    if (tour.status === 'suspended' || tour.status === 'expired') {
      showWarning(`This package is currently ${tour.status} and cannot be booked.`, 'Status Restricted');
      setIsAdding(false);
      return;
    }

    // Block booking if scheduled but not yet open
    if (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) {
      // For the card, we'll redirect to details where the fancy glass modal is
      navigate(`/tours/${tour.id}?show_scheduled=true`);
      setIsAdding(false);
      return;
    }

    // Block booking if booking window has closed
    if (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) {
      showWarning('Booking for this safari has closed.', 'Booking Closed');
      setIsAdding(false);
      return;
    }

    if (onAddToSafariBuilder) {
      onAddToSafariBuilder(tour);
    } else if (isBuilderOpen && addTourToBuilder) {
      addTourToBuilder(tour);
    } else {
      openBuilder([tour]);
    }

    setTimeout(() => setIsAdding(false), 200);
  }, [currentUser, isAuthenticated, tour, onAddToSafariBuilder, isBuilderOpen, addTourToBuilder, openBuilder, refreshUser, setShowAuthModal, isAdding]);

  const handleRemoveFromCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!cartItemId) return;

    try {
      await refreshUser();
      if (!currentUser || !isAuthenticated) {
        setShowAuthModal(true);
        return;
      }

      await customSafariService.removeFromCart(cartItemId, currentUser.id);
      setIsInCart(false);
      setCartItemId(null);
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Error removing from cart:', error);
      showError('Failed to remove from cart. Please try again.', 'Update Failed');
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite?.(tour.id);
  };

  const handleShareClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (navigator.share) {
      try {
        await navigator.share({
          title: tour.title,
          text: tour.shortDescription,
          url: `${window.location.origin}/tours/${tour.id}`
        });
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.log('Share failed:', error);
        }
      }
    } else {
      const shareUrl = `${window.location.origin}/tours/${tour.id}`;
      const shareText = `${tour.title} - ${tour.shortDescription}`;

      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
          showSuccess('Tour link copied to clipboard!', 'Share Event');
        } catch (err) {
          console.log('Clipboard write failed:', err);
        }
      }
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-primary-100 text-primary-800';
      case 'Moderate': return 'bg-accent-100 text-accent-800';
      case 'Challenging': return 'bg-red-100 text-red-800';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };


  const SparklesIcon = (props: any) => (
    <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  );

  return (
    <div
      data-tour-id={tour?.id}
      data-tour-title={tour?.title}
      className={`relative flex flex-col h-full rounded-2xl overflow-hidden transition-all duration-300 group ${actualTheme === 'dark'
        ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/30'
        : 'bg-white border border-neutral-200/50'
        } floating-card hover:floating-card-hover shadow-modern hover:shadow-modern-lg`}
    >
      <Link
        to={`/tours/${tour.id}`}
        className="absolute inset-0 z-10"
        aria-label={`View details for ${tour.title}`}
      />

      <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
        {tour.images && tour.images.length > 0 && tour.images[0] ? (
          <img
            src={tour.images[0]}
            alt={tour.title}
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${actualTheme === 'dark' ? 'bg-slate-900 border-b border-slate-700' : 'bg-placeholder-blue border-b border-blue-50'}`}>
            <div className="text-center">
              <MapPinIcon className={`w-10 h-10 mx-auto mb-2 opacity-50 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-blue-200'}`} />
              <p className={`text-[10px] font-bold uppercase tracking-widest ${actualTheme === 'dark' ? 'text-slate-600' : 'text-blue-300'}`}>No Image Available</p>
            </div>
          </div>
        )}

        <div className="absolute top-4 left-4 flex flex-col gap-1.5 z-20 max-w-[calc(100%-100px)]">
          <span 
            className="bg-primary-900 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap truncate shadow-lg backdrop-blur-md border border-white/20 inline-block"
          >
            {tour.category}
          </span>
          <span className={`${getDifficultyColor(tour.difficulty || '')} px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap truncate shadow-lg backdrop-blur-md border border-white/10 inline-block w-fit`}>
            {tour.difficulty}
          </span>
        </div>

        <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
          <button
            onClick={handleFavoriteClick}
            className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 transition-all duration-200"
          >
            {isFavorite ? (
              <HeartSolidIcon className="h-5 w-5 text-red-500" />
            ) : (
              <HeartIcon className="h-5 w-5 text-gray-600 hover:text-red-500" />
            )}
          </button>
          <button
            onClick={handleShareClick}
            className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 transition-all duration-200"
          >
            <ShareIcon className="h-5 w-5 text-gray-600 hover:text-blue-500" />
          </button>
        </div>

        <div className="absolute bottom-4 right-4">
          <div className="bg-white rounded-full px-3 py-1 flex items-center gap-1 shadow-sm">
            <StarSolidIcon className="h-4 w-4 text-accent-500" />
            <span className="text-sm font-medium text-primary-900">{tour.rating}</span>
            <span className="text-xs text-neutral-500">({tour.reviewCount})</span>
          </div>
        </div>

        {/* Status Overlays */}
        {tour.status === 'suspended' && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px] flex items-center justify-center z-10 border-4 border-yellow-500/30 transition-all group-hover:bg-white/10">
            <span className="bg-yellow-100 text-yellow-800 text-sm font-bold px-4 py-2 rounded-full shadow-lg transform -rotate-6">Coming Soon</span>
          </div>
        )}

        {tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate) && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center z-30 transition-all duration-500">
            <div className="text-center text-white p-4 scale-90 sm:scale-100">
              <CalendarDaysIcon className="h-10 w-10 mx-auto mb-2 text-primary-400" />
              <h4 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary-100">Booking Opens In</h4>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20 shadow-2xl">
                <CountdownTimer
                  targetDate={tour.bookingOpenDate}
                  showLabels={true}
                  className="text-white"
                />
              </div>
            </div>
          </div>
        )}

        {tour.status === 'expired' && (
          <div className="absolute inset-0 bg-gray-500/40 grayscale flex items-center justify-center z-10">
            <span className="bg-black/60 text-white text-sm font-bold tracking-widest uppercase px-4 py-2 rounded">Expired</span>
          </div>
        )}
      </div>

      <div className="p-4 sm:p-5 md:p-6 relative z-20 flex-grow flex flex-col">
        <div className="mb-2 sm:mb-3">
          <h3 className={`text-lg sm:text-xl font-bold mb-1 sm:mb-2 line-clamp-2 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'}`}>{tour.title}</h3>
          <div className={`flex items-center ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
            <MapPinIcon className={`h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 flex-shrink-0 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-primary-600'}`} />
            <span className="text-xs sm:text-sm truncate">{tour.location?.name || 'Unknown Location'}</span>
          </div>
        </div>

        <p className={`mb-3 sm:mb-4 line-clamp-2 text-xs sm:text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>{tour.shortDescription}</p>

        <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-3 sm:mb-4">
          <div className={`flex items-center ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
            <ClockIcon className={`h-4 w-4 mr-2 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-primary-600'}`} />
            <span className="text-sm">{tour.duration}</span>
          </div>
          <div className={`flex items-center ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
            <SparklesIcon className={`h-4 w-4 mr-2 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-primary-600'}`} />
            <span className="text-sm font-semibold text-primary-600 dark:text-primary-400 uppercase">{pricingContext.tier} Tier</span>
          </div>
        </div>

        {/* Tier Selector */}
        <div className="mb-3 relative z-30" onClick={e => e.stopPropagation()}>
          <span className={`text-[10px] font-semibold mb-1.5 block uppercase tracking-wider ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'}`}>
            Select Experience Tier
          </span>
          <div className="flex gap-1 sm:gap-1.5">
            {([PricingTier.LUXURY, PricingTier.STANDARD, PricingTier.BUDGET] as PricingTier[]).map((tier) => {
              const isSelected = selectedTier === tier;
              return (
                <button
                  key={tier}
                  type="button"
                  onClick={(e) => handleTierChange(e, tier)}
                  aria-pressed={isSelected}
                  className={`flex-1 relative py-1 px-1.5 rounded-full border text-[9px] sm:text-[10px] font-bold uppercase tracking-wide
                    transition-all duration-200 ease-out select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500
                    ${isSelected
                      ? actualTheme === 'dark'
                        ? 'bg-primary-600 border-primary-500 text-white shadow-md scale-[1.04]'
                        : 'bg-primary-800 border-primary-700 text-white shadow-md scale-[1.04]'
                      : actualTheme === 'dark'
                        ? 'bg-slate-700/60 border-slate-600 text-slate-300 hover:bg-slate-600/80 hover:text-white'
                        : 'bg-neutral-50 border-neutral-300 text-neutral-600 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-800'
                    }`}
                >
                  {isSelected && (
                    <span className="absolute inset-0 rounded-full animate-ping opacity-20 bg-primary-400 pointer-events-none" />
                  )}
                  {tier}
                </button>
              );
            })}
          </div>
        </div>

        {/* Price Display — fades smoothly on tier change */}
        <div key={priceKey} className="flex flex-col relative z-20 transition-opacity duration-300" style={{ animation: 'priceFadeIn 0.25s ease-out' }}>
          {hasDiscount && originalUnitPricing && unitCalculatedPricing && !isLoadingPrice && (
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-semibold uppercase tracking-wider ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`}>WAS:</span>
              <span className={`text-sm line-through font-medium ${actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-400'}`}>
                {currencyService.formatCurrency(originalUnitPricing.total, originalUnitPricing.currency)}
              </span>
              <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                -{Math.round((1 - unitCalculatedPricing.total / originalUnitPricing.total) * 100)}%
              </span>
            </div>
          )}
          <div className="flex items-baseline gap-1.5 mb-1">
            {hasDiscount && !isLoadingPrice && (
              <span className={`text-[10px] font-bold uppercase tracking-wider ${actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>NOW:</span>
            )}
            <div className={`text-2xl font-bold min-h-[32px] flex items-center ${
              hasDiscount
                ? actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'
                : actualTheme === 'dark' ? 'text-white' : 'text-primary-800'
            }`}>
              {isLoadingPrice ? (
                <div className="h-6 w-28 animate-pulse bg-current opacity-20 rounded-md" />
              ) : unitCalculatedPricing?.total != null ? (
                currencyService.formatCurrency(unitCalculatedPricing.total, unitCalculatedPricing.currency)
              ) : hasTierVariants ? (
                <span className="text-sm font-medium opacity-60 italic">Calculating…</span>
              ) : (
                <span className="text-sm font-bold text-accent-600 uppercase tracking-tighter">Pricing Pending</span>
              )}
            </div>
            {hasTierVariants && !isLoadingPrice && (
              <span className={`text-xs font-medium ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`}>
                / adult
              </span>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">
              {hasTierVariants
                ? (pricingContext.nationalityType === 'LOCAL' ? 'Local Resident Rate' : 'International Rate')
                : 'Configure Tiers in Admin'}
            </div>
            {calculatedPricing && (pax.adults > 1 || pax.children > 0) && (
              <div className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold min-h-[26px] border shadow-sm transition-all duration-300 ${
                actualTheme === 'dark'
                  ? 'bg-primary-900/40 text-primary-300 border-primary-700/50'
                  : 'bg-primary-50 text-primary-800 border-primary-200'
              }`}>
                <span className="opacity-70 mr-1.5 uppercase text-[10px] tracking-tight">Total</span>
                {currencyService.formatCurrency(calculatedPricing.total, calculatedPricing.currency)}
                <span className="ml-1.5 opacity-60 font-semibold text-[10px]">
                  ({pax.adults} {pax.adults === 1 ? 'Adult' : 'Adults'}
                  {pax.children > 0 && ` & ${pax.children} ${pax.children === 1 ? 'Child' : 'Children'}`})
                </span>
              </div>
            )}
            {!calculatedPricing && (pax.adults > 1 || pax.children > 0) && (
              <div className="h-[26px] w-32 animate-pulse bg-neutral-200 dark:bg-slate-700 rounded-lg opacity-30" />
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 relative z-20">
          <div className="flex gap-2 w-full">
            <Link
              to={`/tours/${tour.id}`}
              onClick={(e) => e.stopPropagation()}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${actualTheme === 'dark' ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'}`}
            >
              View Details
            </Link>
            {isInCart ? (
              <button
                onClick={handleRemoveFromCart}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 bg-green-600 hover:bg-green-700 text-white shadow-sm"
              >
                <CheckCircleIcon className="h-3.5 w-3.5" />
                Added
              </button>
            ) : tour.status === 'suspended' ? (
              <button
                disabled
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 bg-yellow-100 text-yellow-800 cursor-not-allowed border border-yellow-200"
              >
                Soon
              </button>
            ) : (tour.status === 'scheduled' && tour.bookingOpenDate && isFuture(tour.bookingOpenDate)) ? (
              <button
                onClick={() => navigate(`/tours/${tour.id}`)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 bg-primary-100 text-primary-700 border border-primary-200"
              >
                <CalendarDaysIcon className="h-3.5 w-3.5" />
                Waitlist
              </button>
            ) : (tour.bookingCloseDate && isPast(tour.bookingCloseDate)) ? (
              <button
                disabled
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 bg-red-100 text-red-800 cursor-not-allowed border border-red-200"
              >
                Closed
              </button>
            ) : tour.status === 'expired' ? (
              <button
                disabled
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 bg-gray-200 text-gray-500 cursor-not-allowed"
              >
                Expired
              </button>
            ) : (
              <button
                onClick={handleAddToSafari}
                disabled={isAdding}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 active:scale-95 ${actualTheme === 'dark' ? 'bg-primary-700 hover:bg-primary-600 text-white' : 'bg-primary-800 hover:bg-primary-700 text-white shadow-sm'}`}
              >
                {isAdding ? '...' : <><PlusIcon className="h-3.5 w-3.5" /> Builder</>}
              </button>
            )}
          </div>
        </div>

        <div className="mt-auto pt-4 border-t border-neutral-200 flex items-center justify-between text-sm transition-all z-20 relative">
          <span className="text-neutral-600 dark:text-slate-400">{tour.availability?.length > 0 ? 'Available' : 'Check availability'}</span>
          <span className="text-primary-600 dark:text-primary-400 font-medium">{tour.availability?.length > 0 ? `${tour.availability.length} dates` : 'Contact us'}</span>
        </div>
      </div>

      {showAuthModal && (
        <UserAuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={async () => {
            setShowAuthModal(false);
            setTimeout(() => {
              if (onAddToSafariBuilder) onAddToSafariBuilder(tour);
              else if (isBuilderOpen && addTourToBuilder) addTourToBuilder(tour);
              else openBuilder([tour]);
            }, 100);
          }}
        />
      )}
    </div>
  );
};

export default TourCard;
